// Global packages and components
import { useRouteLoaderData } from '@remix-run/react'
import {
  Button,
  ContentEditor,
  NavLink,
  SocialChannels,
  Heading,
  Image,
} from '~/components'
import { RootDataProps } from '~/types'

// Main export
const Footer = () => {
  const { site } = useRouteLoaderData('root') as RootDataProps

  const colOneNav = site?.settings?.footerColOneNavigation
    ? site.navigations.find(
        e => e._id == site.settings.footerColOneNavigation._ref
      )
    : null
  const colTwoNav = site?.settings?.footerColTwoNavigation
    ? site.navigations.find(
        e => e._id == site.settings.footerColTwoNavigation._ref
      )
    : null
  const colThreeNav = site?.settings?.footerColThreeNavigation
    ? site.navigations.find(
        e => e._id == site.settings.footerColThreeNavigation._ref
      )
    : null
  const legalNav = site?.settings?.legalNavigation
    ? site.navigations.find(e => e._id == site.settings.legalNavigation._ref)
    : null

  // Accreditation logos, managed under Global → Footer in Sanity
  const accreditations = site?.global?.accreditations

  return (
    <div className="bg-emerald-green pb-20 pt-10 text-white">
      <div className="container">
        <h2 className="sr-only">Site information</h2>
        <div className="mb-6 grid gap-6 md:mb-10 md:grid-cols-2 md:gap-10 lg:grid-cols-4">
          <div>
            {site?.global?.contentMarketplace && (
              <div className="mb-10">
                <div className="mb-6 [&_a]:text-vibrant-green">
                  <ContentEditor content={site.global.contentMarketplace} />
                </div>
                {site?.global?.marketplaceLink && (
                  <Button
                    to={site.global.marketplaceLink}
                    title="Learn more"
                    srOnlyText=" about HSS ProService Marketplace"
                    hasArrow
                  />
                )}
              </div>
            )}
            {site?.global?.contentCareers && (
              <div className="mb-10 [&_a]:text-vibrant-green">
                <ContentEditor content={site.global.contentCareers} />
              </div>
            )}
            {site?.global?.socialChannels && (
              <>
                <Heading
                  tag="h4"
                  size="h7"
                  font={false}
                >
                  Our Socials
                </Heading>
                <SocialChannels items={site.global.socialChannels} />
              </>
            )}
          </div>
          <div>
            {site?.global?.columnOneTitle && (
              <Heading
                tag="h4"
                size="h7"
                font={false}
              >
                {site.global.columnOneTitle}
              </Heading>
            )}
            {colOneNav?.items && (
              <ul>
                {colOneNav.items.map((e, i) => (
                  <li
                    className="mb-3"
                    key={i}
                  >
                    <NavLink
                      {...e}
                      className="text-sm"
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            {site?.global?.columnTwoTitle && (
              <Heading
                tag="h4"
                size="h7"
                font={false}
              >
                {site.global.columnTwoTitle}
              </Heading>
            )}
            {colTwoNav?.items && (
              <ul>
                {colTwoNav.items.map((e, i) => (
                  <li
                    className="mb-3"
                    key={i}
                  >
                    <NavLink
                      {...e}
                      className="text-sm"
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            {site?.global?.columnThreeTitle && (
              <Heading
                tag="h4"
                size="h7"
                font={false}
              >
                {site.global.columnThreeTitle}
              </Heading>
            )}
            {colThreeNav?.items && (
              <ul>
                {colThreeNav.items.map((e, i) => (
                  <li
                    className="mb-3"
                    key={i}
                  >
                    <NavLink
                      {...e}
                      className="text-sm"
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="mb-6 md:mb-10 md:flex md:justify-between">
          {legalNav?.items && (
            <ul className="mb-6 md:mb-0 md:flex md:gap-5">
              {legalNav.items.map((e, i) => (
                <li
                  className="mb-3"
                  key={i}
                >
                  <NavLink
                    {...e}
                    className="text-sm text-gray-300"
                  />
                </li>
              ))}
              <li>
                <button
                  className="text-sm text-gray-300"
                  onClick={() => {
                    if (
                      window?.Cookiebot &&
                      typeof window.Cookiebot?.renew === 'function'
                    ) {
                      window.Cookiebot.renew() // Opens the cookie consent dialog
                    } else {
                      console.warn('Cookiebot not loaded yet')
                    }
                  }}
                >
                  Cookie settings
                </button>
              </li>
              <li className="mb-3">
                <a
                  href="/entitymap.html"
                  className="text-sm text-gray-300"
                >
                  EntityMap
                </a>
              </li>
            </ul>
          )}
          <div>
            {site?.global?.cardImages?.images && (
              <ul className="-mx-2 flex flex-wrap">
                {site.global.cardImages.images.map((e, i) => (
                  <li
                    className="px-2"
                    key={i}
                  >
                    {e.asset && (
                      <Image
                        image={e}
                        customSize={50}
                        className="w-12"
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="md:flex md:flex-row-reverse md:justify-between md:gap-10">
          {!!accreditations?.length && (
            <ul className="mb-6 flex shrink-0 flex-wrap gap-x-8 gap-y-4 md:mb-0 md:justify-end">
              {accreditations.map(({ _key, image, link }) => {
                if (!image.asset) return null

                const logo = (
                  <Image
                    image={image}
                    customSize={160}
                    className="w-20"
                  />
                )

                return (
                  <li key={_key}>
                    {link ? (
                      <a
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {logo}
                      </a>
                    ) : (
                      logo
                    )}
                  </li>
                )
              })}
            </ul>
          )}
          <div>
            {site?.global?.copyright && (
              <p className="mb-2 text-sm text-gray-300">
                {site.global.copyright}
              </p>
            )}
            {site?.global?.companyNo && (
              <p className="mb-2 text-sm text-gray-300">
                {site.global.companyNo}
              </p>
            )}
            {site?.global?.address && (
              <p className="mb-2 text-sm text-gray-300">
                {site.global.address}
              </p>
            )}
            {site?.global?.disclaimer && (
              <p className="mb-0 text-sm text-gray-300">
                {site.global.disclaimer}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Footer
